import { EmptyState, ErrorState, LoadingState, WithHalfdot } from "@elyxeer/ui";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Screen } from "~/components/ui/Screen";
import { InvitationsPanel } from "~/components/users/InvitationsPanel";
import { InviteDialog } from "~/components/users/InviteDialog";
import { useLocale } from "~/i18n";
import { fetchAdminUsers } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { AdminUser, MembershipStatus } from "~/types/users-admin";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

type ActivityFilter = "all" | "active" | "inactive";

const MEMBERSHIP_COLOR: Record<
  MembershipStatus,
  "default" | "success" | "warning"
> = {
  active: "success",
  none: "default",
  unknown: "warning",
};

/** Searchable account list plus the complete lifecycle of override invitations. */
export function UsersRoute() {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const [activity, setActivity] = useState<ActivityFilter>("all");
  const [overrideOnly, setOverrideOnly] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim() || undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const isActive = activity === "all" ? undefined : activity === "active";
  const hasMembershipOverride = overrideOnly ? true : undefined;

  const query = useInfiniteQuery({
    queryKey: queryKeys.users({ search, isActive, hasMembershipOverride }),
    queryFn: ({ pageParam, signal }) =>
      fetchAdminUsers({
        search,
        isActive,
        hasMembershipOverride,
        limit: PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const users = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  const dateOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  return (
    <Screen maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{t("users.title")}</WithHalfdot>
          </Typography>
          <Typography
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1 }}
          >
            {t("users.subtitle")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddAltIcon />}
          onClick={() => setInviteOpen(true)}
          data-testid="users.new-invite"
        >
          {t("users.newInvite")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 2,
        }}
      >
        <Tabs
          value={activity}
          onChange={(_event, value: ActivityFilter) => setActivity(value)}
          sx={{ minWidth: 300 }}
        >
          <Tab label={t("users.filter.all")} value="all" />
          <Tab label={t("users.filter.active")} value="active" />
          <Tab label={t("users.filter.inactive")} value="inactive" />
        </Tabs>

        <FormControlLabel
          control={
            <Switch
              checked={overrideOnly}
              onChange={(event) => setOverrideOnly(event.target.checked)}
              data-testid="users.filter-override"
            />
          }
          label={t("users.filter.overrideOnly")}
          sx={{ marginLeft: { lg: "auto" } }}
        />

        <TextField
          label={t("users.search")}
          type="search"
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            },
            htmlInput: { "data-testid": "users.search" },
          }}
          sx={{ width: { xs: "100%", sm: 340 } }}
        />
      </Box>

      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : users.length === 0 ? (
        <EmptyState
          message={t(
            search || activity !== "all" || overrideOnly
              ? "users.emptyFilter"
              : "users.empty",
          )}
        />
      ) : (
        <>
          <Typography
            variant="T3"
            sx={{ color: "text.muted", marginBottom: 1 }}
          >
            {t("users.count", { loaded: users.length, total })}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table data-testid="users.table" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "34%" }}>
                    {t("users.columns.user")}
                  </TableCell>
                  <TableCell>{t("users.columns.access")}</TableCell>
                  <TableCell>{t("users.columns.membership")}</TableCell>
                  <TableCell>{t("users.columns.createdAt")}</TableCell>
                  <TableCell>{t("users.columns.lastLoginAt")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    formatDate={(value) => formatDate(value, dateOptions)}
                    onOpen={() => void navigate(`/users/${user.id}`)}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>

          {query.hasNextPage ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <Button
                variant="outlined"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                data-testid="users.load-more"
              >
                {t("users.loadMore")}
              </Button>
            </Box>
          ) : null}
        </>
      )}

      <InvitationsPanel />
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </Screen>
  );
}

function UserRow({
  user,
  formatDate,
  onOpen,
}: {
  user: AdminUser;
  formatDate: (value: string) => string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();

  return (
    <TableRow
      hover
      onClick={onOpen}
      data-testid={`users.row-${user.id}`}
      sx={{ cursor: "pointer" }}
    >
      <TableCell>
        <Typography variant="L1" sx={{ color: "text.primary" }}>
          {user.display_name || user.email}
        </Typography>
        {user.display_name ? (
          <Typography variant="T3" component="p" sx={{ color: "text.muted" }}>
            {user.email}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          <Chip
            size="small"
            variant="outlined"
            color={user.is_active ? "success" : "default"}
            label={t(user.is_active ? "users.active" : "users.inactive")}
          />
          {user.is_admin ? (
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label={t("users.admin")}
            />
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {user.has_membership_override ? (
            <Chip
              size="small"
              color="primary"
              label={t("users.membership.override")}
            />
          ) : null}
          <Chip
            size="small"
            variant="outlined"
            color={MEMBERSHIP_COLOR[user.membership_status]}
            label={t(`users.membership.${user.membership_status}`)}
          />
        </Box>
      </TableCell>
      <TableCell>
        <time dateTime={user.created_at}>{formatDate(user.created_at)}</time>
      </TableCell>
      <TableCell>
        {user.last_login_at ? (
          <time dateTime={user.last_login_at}>
            {formatDate(user.last_login_at)}
          </time>
        ) : (
          t("common.never")
        )}
      </TableCell>
    </TableRow>
  );
}
